import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';

interface ToolPreviewProps {
  toolId: string;
  prefix: string;
  onNavigateToConfig: () => void;
  toolDetails?: any;
  toolType?: string;
}

export const ToolPreview: React.FC<ToolPreviewProps> = ({
  onNavigateToConfig,
  toolDetails,
  toolType
}) => {
  const { t } = useTranslation();
  
  if (!toolDetails || typeof toolDetails !== 'object') return null;

  const sampleItems: string[] = toolDetails.sample_content?.questions_or_snippets || [];
  const keyInsights: string[] = toolDetails.key_insights || [];
  const recommended: string[] = toolDetails.recommended_actions || [];
  const howToUse: string | undefined = toolDetails.usage?.how_to_use;
  const whenToUse: string | undefined = toolDetails.usage?.when_to_use;
  const expectedOutcome: string | undefined = toolDetails.expected_outcome;
  const additionalNotes: string | undefined = toolDetails.additional_notes;

  // Determine CTA text based on tool type
  const getCTAText = () => {
    if (toolType === 'conversation') {
      return {
        title: t('tool.detail.preview.cta.conversation.title'),
        subtitle: t('tool.detail.preview.cta.conversation.subtitle')
      };
    }
    return {
      title: t('tool.detail.preview.cta.survey.title'),
      subtitle: t('tool.detail.preview.cta.survey.subtitle')
    };
  };

  const ctaText = getCTAText();

  return (
    <div className="space-y-8">
      {/* First Card: Preview Content */}
      <Card className="shadow-elegant">
        <CardHeader>
          <CardTitle className="text-2xl mb-2">{t('tool.detail.preview.title')}</CardTitle>
          <p className="text-muted-foreground">
            {t('tool.detail.preview.subtitle')}
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Sample Questions Preview */}
            {sampleItems.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg mb-3">{t('tool.detail.preview.sampleQuestions')}</h3>
                <div className="space-y-3">
                  {sampleItems.map((item, idx) => (
                    <div key={idx} className="p-4 bg-gradient-subtle rounded-lg border border-primary/10">
                      <p className="text-sm text-muted-foreground">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Results Preview */}
            {keyInsights.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg mb-3">{t('tool.detail.preview.generatedInsights')}</h3>
                <div className="space-y-4">
                  {/* Key Insights */}
                  <div className="p-4 bg-gradient-subtle rounded-lg border border-primary/10">
                    <p className="font-medium text-sm mb-2">{t('tool.detail.preview.keyInsights')}</p>
                    <div className="space-y-2">
                      {keyInsights.map((insight, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1"></div>
                          <p className="text-xs text-muted-foreground">
                            {insight}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recommended Actions */}
                  {recommended.length > 0 && (
                    <div className="p-4 bg-gradient-subtle rounded-lg border border-primary/10">
                      <p className="font-medium text-sm mb-2">{t('tool.detail.preview.recommendedActions')}</p>
                      <div className="space-y-2">
                        {recommended.map((act, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm">
                            <Badge className="text-xs bg-primary/10 text-primary border-border">{idx + 1}</Badge>
                            <span className="text-xs text-muted-foreground">{act}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-8 p-6 bg-primary/5 rounded-lg border border-primary/10">
            <div className="text-center">
              <h4 className="font-semibold mb-2">{ctaText.title}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                {ctaText.subtitle}
              </p>
              <Button 
                onClick={onNavigateToConfig}
                className="bg-gradient-primary text-white"
              >
                {toolType === 'conversation' ? t('tool.detail.preview.cta.conversation.button') : t('tool.detail.preview.cta.survey.button')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Second Card: Usage Information */}
      {(howToUse || whenToUse || expectedOutcome || additionalNotes) && (
        <Card className="shadow-elegant">
          <CardHeader>
            <CardTitle className="text-2xl mb-2">{t('tool.detail.preview.howToUse')}</CardTitle>
            <p className="text-muted-foreground">
              {t('tool.detail.preview.howToUseSubtitle')}
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {howToUse && (
                <div className="p-5 bg-gradient-subtle rounded-lg border border-border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-primary text-lg">📋</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-2">{t('tool.detail.preview.howToUse')}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{howToUse}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {whenToUse && (
                <div className="p-5 bg-gradient-subtle rounded-lg border border-border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-primary text-lg">⏰</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-2">{t('tool.detail.preview.whenToUse')}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{whenToUse}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {expectedOutcome && (
                <div className="p-5 bg-gradient-subtle rounded-lg border border-border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-primary text-lg">🎯</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-2">{t('tool.detail.preview.expectedOutcome')}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{expectedOutcome}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {additionalNotes && (
                <div className="p-5 bg-gradient-subtle rounded-lg border border-border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-primary text-lg">💡</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-2">{t('tool.detail.preview.additionalNotes')}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{additionalNotes}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 